/**
 * The account as a block of plain text, for pasting into a ticket or a Slack
 * thread. Support's most common "export" is a copy-paste, so this is the whole
 * export feature.
 *
 * Plain text on purpose: it survives Zendesk, Slack and email unchanged, where
 * a table or a screenshot does not.
 */
import { providerOrNull } from "./billing-disagreements.js";
import type { BillingSnapshot, UserAccount } from "./user-management.js";

function day(value: string | null): string {
  return value ? value.slice(0, 10) : "—";
}

export function accountSummary(
  account: UserAccount,
  snapshots: BillingSnapshot[] = []
): string {
  const lines: string[] = [];

  lines.push(`TipRanks account — ${account.email}`);
  lines.push(`User ID:      ${account.uid || "—"}`);
  lines.push(`Plan:         ${account.productPlanName ?? "—"} (expires ${day(account.expiration)})`);
  lines.push(`Registered:   ${day(account.registrationDate)}`);
  if (account.origin) lines.push(`Origin:       ${account.origin}`);

  const nextProvider = providerOrNull(account.nextChargePaymentProvider);
  if (nextProvider) lines.push(`Next charge:  via ${nextProvider}`);

  const previous = account.emailHistory
    .filter((e) => !e.isCurrent && e.email.toLowerCase() !== account.email.toLowerCase())
    .map((e) => e.email);
  if (previous.length) lines.push(`Previously:   ${previous.join(", ")}`);

  if (account.addOns.length) {
    const addOns = account.addOns
      .map((a) => (a.isActive === false ? `${a.name} (expired)` : a.name))
      .join(", ");
    lines.push(`Add-ons:      ${addOns}`);
  }

  if (account.activeSubscriptions.length) {
    lines.push("", "TipRanks subscriptions:");
    for (const s of account.activeSubscriptions) {
      const provider = providerOrNull(s.paymentProvider);
      lines.push(
        `  - ${s.name}${provider ? ` via ${provider}` : ""}, expires ${day(s.expiry)}` +
          (s.nextChargeDate ? `, next charge ${day(s.nextChargeDate)}` : "")
      );
    }
  }

  // Only providers that answered — "no subscriptions" and "we couldn't ask"
  // must not read the same way in a ticket someone acts on.
  const answered = snapshots.filter((s) => s.available && s.customers.length > 0);
  if (answered.length) {
    lines.push("", "Payment providers:");
    for (const snap of answered) {
      for (const sub of snap.subscriptions) {
        lines.push(
          `  - ${snap.provider}: ${sub.product ?? sub.id} (${sub.status})` +
            (sub.amount != null ? `, ${sub.amount} ${sub.currency ?? ""}`.trimEnd() : "") +
            (sub.nextChargeDate ? `, next ${day(sub.nextChargeDate)}` : "") +
            ` [${sub.sourceEmail}]`
        );
      }
      const last = snap.events[0];
      if (last) {
        lines.push(
          `  - ${snap.provider}: last ${last.type} ${day(last.date)}` +
            (last.amount != null ? ` ${last.amount} ${last.currency ?? ""}`.trimEnd() : "") +
            (last.reference ? ` (${last.reference})` : "")
        );
      }
      if (snap.subscriptions.length === 0 && snap.events.length === 0) {
        lines.push(`  - ${snap.provider}: customer exists, no subscriptions or payments`);
      }
    }
  }

  const unavailable = snapshots.filter((s) => !s.available).map((s) => s.provider);
  if (unavailable.length) {
    lines.push("", `Not checked: ${unavailable.join(", ")}`);
  }

  return lines.join("\n");
}
